// AssistantTurnItem — llm_call in the activity feed; expand shows assistant text.

import { useEffect, useState } from 'react';
import type { LiveLlmCall, LlmCallPurpose, ProgressStep } from '@localagent/sdk';
import { formatElapsed, useNow } from './use-elapsed';
import { llmStatusPhrase, useLiveLlmCall } from './live-llm-calls';

export interface AssistantTurnItemProps {
  step: ProgressStep;
  /**
   * Parent-driven expand (live focus on latest step, or Show all).
   * Clicking the header toggles a local override until the parent focus changes.
   */
  detailsOpen?: boolean;
}

function formatTokens(tokens: number): string {
  if (tokens >= 1000) return `${(tokens / 1000).toFixed(1)}K`;
  return String(tokens);
}

const PURPOSE_LABEL: Record<LlmCallPurpose, string> = {
  loop: 'Assistant',
  goal_evaluation: 'Goal check',
  process_derivation: 'Planner',
  compaction: 'Summarizer',
};

function runningLabel(step: ProgressStep, live: LiveLlmCall | undefined): string {
  if (live?.phase === 'tool_args') return `Preparing ${live.toolName ?? 'a tool call'}…`;
  if (live?.phase === 'content') return 'Writing response…';
  switch (step.purpose) {
    case 'goal_evaluation':
      return 'Checking the answer…';
    case 'process_derivation':
      return 'Planning the steps…';
    case 'compaction':
      return 'Condensing earlier context…';
    default:
      return 'Thinking…';
  }
}

export function AssistantTurnItem({ step, detailsOpen = false }: AssistantTurnItemProps) {
  const running = step.status === 'running';
  const now = useNow(running);
  const live = useLiveLlmCall(running ? step.callId : undefined);
  const thinking = step.thinkingPreview ?? (live?.reasoning || undefined);
  const response = step.responsePreview ?? (live && live.content ? live.content : undefined);
  const liveToolArgs = live?.phase === 'tool_args' ? live.toolArgs : '';
  const liveChunks = live ? live.reasoningChunks + live.contentChunks + live.toolArgsChunks : 0;
  const toolNames = step.toolNames;
  const model = step.modelName;
  const tokens = step.tokens;
  const hasTools = toolNames != null && toolNames.length > 0;
  const hasBody = Boolean(thinking || response || hasTools || liveToolArgs);

  const meta: string[] = [];
  if (running) meta.push(runningLabel(step, live));
  if (running && step.startedAt) meta.push(formatElapsed(now - new Date(step.startedAt).getTime()));
  if (model) meta.push(model);
  if (running && step.promptTokensEstimate) {
    meta.push(`${formatTokens(step.promptTokensEstimate)} prompt tokens`);
  }
  if (tokens != null && tokens > 0) meta.push(`${formatTokens(tokens)} tokens`);
  if (running && liveChunks > 0) meta.push(`~${formatTokens(liveChunks)} tokens so far`);
  if (step.status === 'failed' && step.error) meta.push('failed');

  const [override, setOverride] = useState<boolean | null>(null);
  useEffect(() => {
    setOverride(null);
  }, [detailsOpen]);

  const open = override ?? detailsOpen;

  return (
    <div
      data-testid="assistant-turn"
      data-details-open={open}
      data-status={step.status ?? 'completed'}
      className="w-full min-w-0 py-0 text-body"
    >
      <button
        type="button"
        data-testid="assistant-turn-toggle"
        aria-expanded={open}
        onClick={() => setOverride(!open)}
        className="flex w-full min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5 text-left hover:text-foreground"
      >
        <span className="font-medium text-info-text">{PURPOSE_LABEL[step.purpose ?? 'loop']}</span>
        {meta.length > 0 && (
          <span className="text-caption text-muted-foreground">{meta.join(' · ')}</span>
        )}
        {running && live && (
          <span
            data-testid="llm-call-status"
            data-llm-state={live.state}
            className={`text-caption ${live.state === 'stalled' || live.state === 'failed' ? 'text-error-text' : 'text-muted-foreground'}`}
          >
            {llmStatusPhrase(live)}
          </span>
        )}
      </button>

      {open && (
        <div className="mt-0.5 min-w-0 space-y-1 pl-1">
          {thinking && (
            <p
              data-testid={live && !step.thinkingPreview ? 'assistant-turn-live-thought' : 'assistant-turn-thought'}
              className="whitespace-pre-wrap break-words text-foreground/90"
            >
              {thinking}
            </p>
          )}

          {response && (
            <p
              data-testid="assistant-turn-response"
              className="whitespace-pre-wrap break-words text-foreground"
            >
              {response}
            </p>
          )}

          {liveToolArgs && (
            <pre
              data-testid="assistant-turn-live-tool-args"
              className="whitespace-pre-wrap break-words text-caption text-muted-foreground"
            >
              {liveToolArgs}
            </pre>
          )}

          {!response && hasTools && (
            <div data-testid="assistant-turn-tools" className="text-caption text-muted-foreground">
              Calling tools: <span className="font-medium text-primary">{toolNames.join(', ')}</span>
            </div>
          )}

          {!hasBody && (
            <div className="text-caption italic text-muted-foreground">Working…</div>
          )}
        </div>
      )}
    </div>
  );
}
